import { useQueryClient } from "@tanstack/react-query";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import Button from "@/components/buttons/Button.tsx";
import Stars from "@/components/detail/Stars.tsx";
import Textfield from "@/components/inputs/Textfield.tsx";

import { ROUTES } from "@/config/routes";
import { FEED_KEYS, useCreateComment } from "@/hooks/api/feed.hooks";
import { useLang } from "@/hooks/useLang";
import useModalRouter from "@/hooks/useModalRouter";
import { onError } from "@/utils/onError";
import { onSuccess } from "@/utils/onSuccess";

import { MODAL_ROUTES } from "../modals/router/ModalRoutes";

interface RateAndComments {
  comment: string;
}

const DetailRateForm = ({ goBack }: { goBack?: boolean }) => {
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };
  const [rating, setRating] = useState(-1);
  const { register, handleSubmit, reset } = useForm<RateAndComments>();
  const { mutate: createComment, isPending } = useCreateComment();
  const queryClient = useQueryClient();
  const lang = useLang();
  const token = localStorage.getItem("splay:token");
  const { openModal } = useModalRouter();
  const navigate = useNavigate();

  const onSubmit = (value: RateAndComments) => {
    if (!token) {
      openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_LOGIN]);
      return;
    }

    if (rating == -1 && value.comment.length == 0) {
      onError({ message: t("rating.select") });
      return;
    }

    createComment(
      {
        id: +id,
        text: value.comment,
        rating: rating + 1,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: FEED_KEYS.contentCommentsList(lang, 2, +id) });
          queryClient.invalidateQueries({ queryKey: FEED_KEYS.contentCommentsList(lang, 18, +id) });
          queryClient.invalidateQueries({ queryKey: FEED_KEYS.contentDetailHistory(lang, +id) });
          onSuccess(t("rating.success"));
          reset();
          setRating(-1);
          if (goBack) {
            navigate(ROUTES.DETAIL.MAIN.replace(":id", id));
          }
        },
        onError,
      },
    );
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={"w-full flex-1 space-y-6 max-2xl:w-full  max-2xl:max-w-none xl:max-w-3xl"}
    >
      <Stars onChange={setRating} rating={rating} />
      <Textfield
        register={register}
        validation={{ required: rating == -1 }}
        name={"comment"}
        rows={7}
        className="h-[9.375rem]"
        placeholder={t("Actions.leave-review")}
      />
      <Button isLoading={isPending} type="submit" className={"w-56 max-2xl:w-full"}>
        {t("Actions.send")}
      </Button>
    </form>
  );
};

export default DetailRateForm;
